import { api } from '../api.ts';
import { section } from '../components.ts';
import { diffView, empty, h, markdown, when } from '../ui.ts';

const TABS = ['overview', 'decisions', 'actions', 'questions', 'people', 'meetings', 'history', 'search'] as const;
type Tab = (typeof TABS)[number];
const ORG_TABS: Tab[] = ['decisions', 'people', 'history', 'search'];

/** One scope: its canonical files, meeting notes, git history with diffs, and search. */
export async function projectView(scope: string, tab: string, arg: string): Promise<HTMLElement> {
  const isProject = scope.includes('/projects/');
  const tabs = isProject ? [...TABS] : [...ORG_TABS, 'inbox' as Tab];
  const current = (tabs.includes(tab as Tab) ? tab : tabs[0]) as Tab;
  const root = h('div', { class: 'page' });
  root.append(
    h('a', { class: 'back', href: '#/knowledge' }, '‹ Knowledge'),
    h('h1', { class: 'page-title' }, isProject ? scope.split('/projects/')[1]! : scope),
    h('p', { class: 'page-sub mono' }, scope),
    h(
      'div',
      { class: 'tabs' },
      ...tabs.map((t) => h('a', { href: `#/project/${scope}/${t}`, class: t === current ? 'active' : '' }, t)),
    ),
  );
  root.append(askBox(scope));
  const body = h('div');
  root.append(body);
  if (current === 'meetings') body.append(await meetings(scope, arg));
  else if (current === 'history') body.append(await history(scope, arg));
  else if (current === 'search') body.append(await search(scope, arg));
  else body.append(await file(scope, `${current}.md`));
  return root;
}

/** Ask her back, typed: POST /ask and show the answer with its citations. */
function askBox(scope: string): HTMLElement {
  const input = h('input', {
    class: 'input',
    placeholder: 'Ask her: what did we decide on pricing?',
    'aria-label': 'Ask about this project',
  });
  const out = h('div');
  const go = async () => {
    const q = input.value.trim();
    if (!q) return;
    out.replaceChildren(h('div', { class: 'skeleton', style: 'height:72px' }));
    try {
      const a = await api.post<{
        answer: string;
        citations: Array<{ file: string; heading?: string; date?: string }>;
        confident: boolean;
      }>('/ask', { question: q, scope });
      const cites = a.citations.map((c) =>
        h(
          'a',
          {
            class: 'link',
            href: `#/project/${scope}/${c.file.replace(/^projects\/[^/]+\//, '').replace(/\.md$/, '')}`,
          },
          `${c.file}${c.date ? ` (${c.date})` : ''}`,
        ),
      );
      out.replaceChildren(
        h(
          'div',
          { class: 'card' },
          h('div', {}, a.answer),
          h(
            'div',
            { class: 'mono muted row' },
            ...(cites.length
              ? cites
              : [h('span', {}, a.confident ? '' : 'not grounded; recorded as an open question')]),
          ),
        ),
      );
    } catch (err) {
      out.replaceChildren(h('p', { class: 'muted' }, err instanceof Error ? err.message : String(err)));
    }
  };
  input.addEventListener('keydown', (e) => (e as KeyboardEvent).key === 'Enter' && void go());
  return h(
    'div',
    { class: 'ask stack' },
    h(
      'div',
      { class: 'inline-form' },
      input,
      h('button', { class: 'btn', type: 'button', onclick: () => void go() }, 'Ask'),
    ),
    out,
  );
}

async function file(scope: string, name: string): Promise<HTMLElement> {
  try {
    const { content } = await api.get<{ content: string }>('/knowledge/file', { scope, file: name });
    const log = await api.get<Array<{ hash: string; date: string; message: string }>>('/knowledge/log', {
      scope,
      file: name,
      limit: '1',
    });
    const wrap = h('div');
    wrap.append(markdown(content));
    if (log[0])
      wrap.append(
        h(
          'p',
          { class: 'muted mono' },
          `last change ${when(log[0].date)} · ${log[0].message} · `,
          h('a', { class: 'link', href: `#/project/${scope}/history/${log[0].hash}` }, log[0].hash),
        ),
      );
    return wrap;
  } catch {
    return empty(`No ${name} yet.`);
  }
}

async function meetings(scope: string, pick: string): Promise<HTMLElement> {
  const files = (await api.get<string[]>('/knowledge/files', { scope }))
    .filter((f) => f.startsWith('meetings/'))
    .sort()
    .reverse();
  if (!files.length) return empty('No meeting notes yet. They are written when a session ends.');
  const chosen = pick && files.includes(`meetings/${pick}`) ? `meetings/${pick}` : files[0]!;
  const list = h(
    'div',
    { class: 'cards' },
    ...files.map((f) =>
      h(
        'a',
        { class: 'card', href: `#/project/${scope}/meetings/${f.slice('meetings/'.length)}` },
        h('div', { class: 'card-title' }, f.slice('meetings/'.length, -3)),
        h('div', { class: 'mono muted' }, f === chosen ? 'showing' : ''),
      ),
    ),
  );
  const wrap = h('div');
  wrap.append(list, section(chosen.slice('meetings/'.length, -3), await file(scope, chosen)));
  return wrap;
}

async function history(scope: string, hash: string): Promise<HTMLElement> {
  const log = await api.get<Array<{ hash: string; date: string; message: string }>>('/knowledge/log', {
    scope,
    limit: '50',
  });
  if (!log.length) return empty('No history yet.');
  const wrap = h('div');
  const table = h(
    'table',
    {},
    h('thead', {}, h('tr', {}, h('th', {}, 'when'), h('th', {}, 'commit'), h('th', {}, 'message'))),
  );
  for (const c of log)
    table.append(
      h(
        'tr',
        { class: 'clickable', onclick: () => (location.hash = `#/project/${scope}/history/${c.hash}`) },
        h('td', { class: 'mono muted' }, when(c.date)),
        h('td', { class: 'mono' }, c.hash),
        h('td', {}, c.message),
      ),
    );
  wrap.append(h('div', { class: 'card table-card' }, table));
  if (hash) {
    try {
      const c = await api.get<{ hash: string; message: string; files: string[]; patch: string }>('/knowledge/commit', {
        scope,
        hash,
      });
      wrap.append(
        h('h2', { class: 'section-title' }, `${c.hash} · ${c.message}`),
        h('p', { class: 'muted mono' }, c.files.join(', ') || 'no files in this scope'),
        diffView(c.patch),
      );
    } catch {
      wrap.append(empty('Unknown commit.'));
    }
  }
  return wrap;
}

async function search(scope: string, q: string): Promise<HTMLElement> {
  const wrap = h('div');
  const input = h('input', {
    class: 'input',
    placeholder: 'Search this project and its organisation…',
    'aria-label': 'Search',
    value: q,
  });
  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter')
      location.hash = `#/project/${scope}/search/${encodeURIComponent(input.value.trim())}`;
  });
  wrap.append(
    h(
      'div',
      { class: 'inline-form' },
      input,
      h(
        'button',
        {
          class: 'btn',
          type: 'button',
          onclick: () => (location.hash = `#/project/${scope}/search/${encodeURIComponent(input.value.trim())}`),
        },
        'Search',
      ),
    ),
  );
  if (q) {
    const hits = await api.get<Array<{ scope: string; file: string; heading: string; snippet: string }>>(
      '/knowledge/search',
      { q, scope, limit: '20' },
    );
    wrap.append(h('h2', { class: 'section-title' }, `${hits.length} hits for "${q}"`));
    if (!hits.length) wrap.append(empty('Nothing recorded about that.'));
    for (const hit of hits) {
      const el = h('div', { class: 'hit' });
      el.append(h('div', { class: 'mono muted' }, `${hit.scope} › ${hit.file} › ${hit.heading}`));
      const snip = h('div');
      snip.innerHTML = hit.snippet
        .replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!)
        .replace(/\[([^\]]+)\]/g, '<mark>$1</mark>');
      el.append(snip);
      wrap.append(el);
    }
  }
  return wrap;
}
